import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, MousePointerClick, Clock, ArrowRight, RefreshCw, Eye, Target } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, subtitle, color }) => (
  <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-5 backdrop-blur-sm" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center`}>
        <Icon className="w-4.5 h-4.5 text-white" />
      </div>
      <span className="text-gray-400 text-sm">{label}</span>
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
    {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
  </div>
);

const BarRow = ({ label, value, maxValue, color }) => (
  <div className="flex items-center gap-3 group">
    <span className="text-sm text-gray-400 w-28 truncate" title={label}>{label}</span>
    <div className="flex-1 h-7 bg-gray-800/60 rounded-lg overflow-hidden relative">
      <div
        className={`h-full ${color} rounded-lg transition-all duration-700 ease-out`}
        style={{ width: `${maxValue > 0 ? (value / maxValue) * 100 : 0}%` }}
      />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-mono text-gray-300">{value}</span>
    </div>
  </div>
);

export default function DemoAnalyticsDashboard() {
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const statsRes = await axios.get(`${API}/demo-analytics/stats`, { headers });
      setStats(statsRes.data);
      try {
        const sessionsRes = await axios.get(`${API}/demo-analytics/sessions`, { headers });
        setSessions(sessionsRes.data.sessions || []);
      } catch {
        setSessions([]);
      }
    } catch (err) {
      console.error('Analytics fetch failed:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="container mx-auto px-4 flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-purple-500 border-t-transparent" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="container mx-auto px-4 py-12 text-center text-gray-500">
        Failed to load analytics. Try refreshing.
      </div>
    );
  }

  const maxFeatureVisits = Math.max(...(stats.feature_popularity || []).map(f => f.visits), 1);
  const maxDropoff = Math.max(...(stats.dropoff_points || []).map(d => d.count), 1);
  const maxUtm = Math.max(...(stats.utm_sources || []).map(u => u.count), 1);

  return (
    <div className="container mx-auto px-4 space-y-6" data-testid="demo-analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-purple-400" />
            Demo Analytics
          </h1>
          <p className="text-gray-500 text-sm mt-1">Track how visitors experience your demo</p>
        </div>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-sm transition-colors disabled:opacity-50"
          data-testid="analytics-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Demo Sessions" value={stats.total_demo_sessions} subtitle="Total visitors who tried demo" color="bg-purple-500/30" />
        <StatCard icon={MousePointerClick} label="Signups" value={stats.total_signups_from_demo} subtitle="Converted from demo" color="bg-emerald-500/30" />
        <StatCard icon={TrendingUp} label="Conversion Rate" value={`${stats.conversion_rate}%`} subtitle="Sessions → Signups" color="bg-cyan-500/30" />
        <StatCard icon={Eye} label="Avg Explored" value={`${stats.avg_features_explored}/8`} subtitle="Features per session" color="bg-amber-500/30" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Feature Popularity */}
        <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-5" data-testid="feature-popularity-chart">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Target className="w-4 h-4 text-purple-400" />
            Feature Popularity
          </h3>
          {(stats.feature_popularity || []).length > 0 ? (
            <div className="space-y-2.5">
              {stats.feature_popularity.map((f, i) => (
                <BarRow key={f.feature} label={f.feature} value={f.visits} maxValue={maxFeatureVisits} color="bg-gradient-to-r from-purple-600 to-purple-400" />
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-sm">No data yet — share your demo link!</p>
          )}
        </div>

        {/* Dropoff Points */}
        <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-5" data-testid="dropoff-chart">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-red-400" />
            Last Feature Before Leaving
          </h3>
          {(stats.dropoff_points || []).length > 0 ? (
            <div className="space-y-2.5">
              {stats.dropoff_points.map((d, i) => (
                <BarRow key={d.feature} label={d.feature || 'none'} value={d.count} maxValue={maxDropoff} color="bg-gradient-to-r from-red-600 to-orange-400" />
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-sm">No dropoff data yet</p>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* UTM Sources */}
        <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-5" data-testid="utm-sources-chart">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <ArrowRight className="w-4 h-4 text-cyan-400" />
            Traffic Sources (UTM)
          </h3>
          {(stats.utm_sources || []).length > 0 ? (
            <div className="space-y-2.5">
              {stats.utm_sources.map((u, i) => (
                <BarRow key={u.source} label={u.source || 'direct'} value={u.count} maxValue={maxUtm} color="bg-gradient-to-r from-cyan-600 to-cyan-400" />
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-sm">Add ?utm_source=reddit to your demo links to track sources</p>
          )}
        </div>

        {/* CTA Breakdown */}
        <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-5" data-testid="cta-breakdown-chart">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <MousePointerClick className="w-4 h-4 text-emerald-400" />
            Conversion CTA Breakdown
          </h3>
          {(stats.conversion_cta_breakdown || []).length > 0 ? (
            <div className="space-y-3">
              {stats.conversion_cta_breakdown.map((c) => {
                const ctaLabels = {
                  signup_banner: 'Banner "Sign Up Free"',
                  signup_expiry: 'Expiry "Create Account"',
                  back_homepage: 'Back to Homepage',
                };
                return (
                  <div key={c.cta} className="flex items-center justify-between bg-gray-800/40 rounded-lg px-4 py-2.5">
                    <span className="text-sm text-gray-300">{ctaLabels[c.cta] || c.cta}</span>
                    <span className="text-sm font-mono text-emerald-400 font-semibold">{c.clicks} clicks</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-600 text-sm">No CTA clicks yet</p>
          )}
        </div>
      </div>

      {/* Recent Sessions */}
      <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-5" data-testid="recent-sessions">
        <h3 className="text-white font-semibold mb-4">Recent Demo Sessions</h3>
        {sessions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 border-b border-gray-800">
                  <th className="text-left pb-2 pr-4">Session</th>
                  <th className="text-left pb-2 pr-4">Source</th>
                  <th className="text-left pb-2 pr-4">Features</th>
                  <th className="text-left pb-2 pr-4">Duration</th>
                  <th className="text-left pb-2">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s, i) => (
                  <tr key={s.session_id || i} className="border-b border-gray-800/50 text-gray-300">
                    <td className="py-2.5 pr-4 font-mono text-xs text-gray-500">{(s.session_id || '').slice(0, 16)}...</td>
                    <td className="py-2.5 pr-4">{s.utm_source || 'direct'}</td>
                    <td className="py-2.5 pr-4">
                      <span className="text-purple-400 font-semibold">{s.features_explored || 0}</span>/8
                    </td>
                    <td className="py-2.5 pr-4">{s.duration || '—'}</td>
                    <td className="py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        s.converted ? 'bg-emerald-500/20 text-emerald-400' :
                        s.expired ? 'bg-red-500/20 text-red-400' :
                        'bg-gray-700 text-gray-400'
                      }`}>
                        {s.converted ? 'Signed Up' : s.expired ? 'Expired' : 'Active'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-600 text-sm">No sessions recorded yet. Share your demo link to start tracking!</p>
        )}
      </div>

      {/* UTM Link Generator */}
      <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-5" data-testid="utm-link-generator">
        <h3 className="text-white font-semibold mb-3">Quick UTM Links for Ads</h3>
        <p className="text-gray-500 text-sm mb-4">Copy these links for your marketing campaigns — each tracks the source automatically</p>
        <div className="space-y-2">
          {[
            { label: 'Reddit', source: 'reddit', color: 'text-orange-400' },
            { label: 'Product Hunt', source: 'producthunt', color: 'text-amber-400' },
            { label: 'IndieHackers', source: 'indiehackers', color: 'text-cyan-400' },
            { label: 'LinkedIn', source: 'linkedin', color: 'text-blue-400' },
            { label: 'Twitter/X', source: 'twitter', color: 'text-gray-300' },
          ].map(({ label, source, color }) => {
            const url = `${window.location.origin}?utm_source=${source}&utm_medium=social&demo=true`;
            return (
              <div key={source} className="flex items-center gap-3 bg-gray-800/40 rounded-lg px-4 py-2.5">
                <span className={`text-sm font-medium ${color} w-28`}>{label}</span>
                <code className="flex-1 text-xs text-gray-400 truncate">{url}</code>
                <button
                  onClick={() => { navigator.clipboard.writeText(url); }}
                  className="text-xs text-purple-400 hover:text-purple-300 font-medium whitespace-nowrap"
                  data-testid={`copy-utm-${source}`}
                >
                  Copy
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
